import { Section } from '@/types';
import { ResponsiveBackgroundImage } from '@/types/sections';

/**
 * Extract all media URLs from a section
 */
export function extractMediaUrlsFromSection(section: Section): string[] {
  const urls: string[] = [];
  
  if (!section.isActive || !section.sectionData) {
    return urls;
  }

  const sectionData = section.sectionData;
  
  // Helper to normalize and add URLs
  const addUrl = (url: string | undefined) => {
    if (url && typeof url === 'string' && url.trim()) {
      // Normalize /api/v1/media/download to /api/media/download
      const normalized = url.includes('/api/v1/media/download')
        ? url.replace('/api/v1/media/download', '/api/media/download')
        : url;
      if (!urls.includes(normalized)) {
        urls.push(normalized);
      }
    }
  };

  // Helper to extract from ResponsiveBackgroundImage
  const extractFromResponsive = (bg: ResponsiveBackgroundImage | string | undefined) => {
    if (!bg) return;
    
    if (typeof bg === 'string') {
      addUrl(bg);
    } else if (typeof bg === 'object') {
      addUrl(bg.desktop);
      addUrl(bg.mobile);
    }
  };

  // Extract backgroundVideo (priority - videos load first)
  extractFromResponsive(
    sectionData.backgroundVideo as ResponsiveBackgroundImage | string | undefined
  );

  // Extract backgroundImage
  extractFromResponsive(
    sectionData.backgroundImage as ResponsiveBackgroundImage | string | undefined
  );

  // Extract backgroundMedia (new unified field for videos/images)
  extractFromResponsive(
    sectionData.backgroundMedia as ResponsiveBackgroundImage | string | undefined
  );

  // Extract image
  if (sectionData.image && typeof sectionData.image === 'string') {
    addUrl(sectionData.image);
  }

  // Extract images array
  if (Array.isArray(sectionData.images)) {
    sectionData.images.forEach((img) => {
      if (typeof img === 'string') {
        addUrl(img);
      }
    });
  }

  // Extract videoUrl
  if (sectionData.videoUrl && typeof sectionData.videoUrl === 'string') {
    addUrl(sectionData.videoUrl);
  }

  // Extract videoThumbnail
  if (sectionData.videoThumbnail && typeof sectionData.videoThumbnail === 'string') {
    addUrl(sectionData.videoThumbnail);
  }

  // Extract from nested objects (solutions, features, services, etc.)
  const extractFromArray = (arr: unknown[] | undefined, prop: string) => {
    if (!Array.isArray(arr)) return;
    arr.forEach((item) => {
      if (typeof item === 'object' && item !== null) {
        const value = (item as Record<string, unknown>)[prop];
        if (typeof value === 'string') {
          addUrl(value);
        }
      }
    });
  };

  // Extract from common nested arrays with 'image' property
  extractFromArray(sectionData.solutions as unknown[] | undefined, 'image');
  extractFromArray(sectionData.features as unknown[] | undefined, 'image');
  extractFromArray(sectionData.services as unknown[] | undefined, 'image');
  extractFromArray(sectionData.items as unknown[] | undefined, 'image');
  extractFromArray(sectionData.cards as unknown[] | undefined, 'image');
  extractFromArray(sectionData.testimonials as unknown[] | undefined, 'image');
  extractFromArray(sectionData.team as unknown[] | undefined, 'image');
  extractFromArray(sectionData.gallery as unknown[] | undefined, 'image');
  extractFromArray(sectionData.slides as unknown[] | undefined, 'image');
  extractFromArray(sectionData.projects as unknown[] | undefined, 'image');
  extractFromArray(sectionData.partners as unknown[] | undefined, 'image');
  extractFromArray(sectionData.logos as unknown[] | undefined, 'image');
  
  // Also extract 'video' properties from arrays
  extractFromArray(sectionData.solutions as unknown[] | undefined, 'video');
  extractFromArray(sectionData.features as unknown[] | undefined, 'video');
  extractFromArray(sectionData.items as unknown[] | undefined, 'video');
  extractFromArray(sectionData.cards as unknown[] | undefined, 'video');
  extractFromArray(sectionData.slides as unknown[] | undefined, 'video');
  
  // Extract 'src' properties from arrays
  extractFromArray(sectionData.solutions as unknown[] | undefined, 'src');
  extractFromArray(sectionData.features as unknown[] | undefined, 'src');
  extractFromArray(sectionData.items as unknown[] | undefined, 'src');
  extractFromArray(sectionData.media as unknown[] | undefined, 'src');
  extractFromArray(sectionData.media as unknown[] | undefined, 'url');

  // Helper to extract from component-like objects
  const extractFromComponentContent = (comp: unknown) => {
    if (typeof comp === 'object' && comp !== null) {
      const component = comp as { content?: Record<string, unknown> };
      if (component.content) {
        ['image', 'videoUrl', 'backgroundImage', 'backgroundVideo', 'src', 'video', 'thumbnail'].forEach((prop) => {
          const value = component.content?.[prop];
          if (typeof value === 'string') {
            addUrl(value);
          } else if (typeof value === 'object' && value !== null) {
            extractFromResponsive(value as ResponsiveBackgroundImage);
          }
        });
      }
    }
  };

  // Extract from components if present (at section root level)
  if (Array.isArray(section.components)) {
    section.components.forEach(extractFromComponentContent);
  }

  // Extract from sectionComponents if present (nested in sectionData)
  if (Array.isArray(sectionData.sectionComponents)) {
    (sectionData.sectionComponents as unknown[]).forEach(extractFromComponentContent);
  }

  return urls;
}

